/* Auditor Information — the rules, transcribed. */

/* The two stored shapes, transcribed from the live save: financial_year_end ← formatDateToISO(date) → "YYYY-MM-DD" last_audited ← extractYearFromDate(date) →... */

export const YEAR_END_HINT = "MM/DD";

/* "2025-12-30" → "12/30". Anything unparseable shows as nothing rather than
   as a broken string. */
export const toYearEndDisplay = (stored) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(stored || ""));
  return match ? `${match[2]}/${match[3]}` : "";
};

/* The year to file a MM/DD against. */
export const yearFor = (stored) => {
  const match = /^(\d{4})-/.exec(String(stored || ""));
  return match ? match[1] : String(new Date().getFullYear());
};

/* "12/30" + "2025" → "2025-12-30". */
export const fromYearEndDisplay = (display, stored) => {
  const match = /^(\d{2})\/(\d{2})$/.exec(String(display || "").trim());
  if (!match) return "";
  return `${yearFor(stored)}-${match[1]}-${match[2]}`;
};

/* A real calendar date, not just four digits in the right places — "02/30"
   passes the shape and is not a day. Checked against a non-leap year so 02/29
   is refused too, since the year the record is filed under can change. */
export const isRealMonthDay = (display) => {
  const match = /^(\d{2})\/(\d{2})$/.exec(String(display || "").trim());
  if (!match) return false;
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (month < 1 || month > 12 || day < 1) return false;
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
};

/* Transcribed: the live year picker carries `maxDate={new Date()}`, so a
   future audit cannot be recorded. */
export const isPlausibleYear = (value) => {
  const text = String(value || "").trim();
  if (!/^\d{4}$/.test(text)) return false;
  const year = Number(text);
  return year >= 1900 && year <= new Date().getFullYear();
};

/* What an existing record seeds the form with. */
export const auditorFromRecord = (record) => ({
  firmName: record?.audit_firm_name || "",
  firmCompanyID:
    record?.audit_firm_companyID_id ?? record?.audit_firm_companyID ?? "",
  yearEnd: toYearEndDisplay(record?.financial_year_end),
  storedYearEnd: record?.financial_year_end || "",
  lastAudited: record?.last_audited ? String(record.last_audited) : "",
  country: record?.country || "",
  state: record?.state || "",
});

export const EMPTY_AUDITOR = {
  firmName: "",
  firmCompanyID: "",
  yearEnd: "",
  storedYearEnd: "",
  lastAudited: "",
  country: "",
  state: "",
};

/* Transcribed: the live save has a whole branch for "every field is blank", which nulls the record out. */
export const isBlankAuditor = ({ firmName, yearEnd, lastAudited, country, state }) =>
  [firmName, yearEnd, lastAudited, country, state].every(
    (v) => String(v ?? "").trim() === ""
  );

/* The live section validates NOTHING, and its save calls `formatDateToISO(financialYearEndDate)` — `date.toISOString()` — on whatever is there. */
export const validateAuditor = (values) => {
  const errors = {};
  if (isBlankAuditor(values)) return errors;

  if (String(values.firmName ?? "").trim() === "") {
    errors.firmName = "Please select the audited firm";
  }

  const yearEnd = String(values.yearEnd ?? "").trim();
  if (yearEnd === "") {
    errors.yearEnd = "Financial year end is required";
  } else if (!isRealMonthDay(yearEnd)) {
    errors.yearEnd = "Enter the month and day as MM/DD";
  }

  const lastAudited = String(values.lastAudited ?? "").trim();
  if (lastAudited === "") {
    errors.lastAudited = "Last audited is required";
  } else if (!/^\d{4}$/.test(lastAudited)) {
    errors.lastAudited = "Enter the year as YYYY";
  } else if (!isPlausibleYear(lastAudited)) {
    errors.lastAudited = "Enter a year that has already passed";
  }

  return errors;
};

/* The payload, field for field. */
export const auditorPayload = (values, { existingStatement = "" } = {}) => {
  const body = new FormData();

  if (isBlankAuditor(values)) {
    body.append("audited_financial_statement", null);
    body.append("audit_firm_name", "");
    body.append("financial_year_end", null);
    body.append("last_audited", null);
    body.append("state", null);
    body.append("country", null);
    body.append("audit_firm_companyID", null);
    return body;
  }

  body.append("audited_financial_statement", existingStatement ?? "");
  body.append("audit_firm_name", values.firmName);
  body.append(
    "financial_year_end",
    fromYearEndDisplay(values.yearEnd, values.storedYearEnd)
  );
  body.append("last_audited", String(values.lastAudited).trim());
  body.append("state", values.state || "");
  body.append("country", values.country || "");
  body.append("audit_firm_companyID", values.firmCompanyID ?? "");
  return body;
};

/* Transcribed request_type strings, verbatim. */
export const AUDITOR_REQUEST_TYPES = {
  create: "Financial statement create draft",
  update: "Financial statement Update draft",
};

export const AUDITOR_FOLLOW = {
  created: "New Audit Firm Added",
  updated: "Audit Firm Updated",
};
